<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="primary" class="mr-1" size="xs" @click="toggle"
        ><i class="fas fa-edit"></i>&nbsp;Edit</b-button
      >
    </slot>

    <b-modal
      @show="resetModal"
      @hidden="resetModal"
      ref="modal-container"
      v-model="modalShow"
      size="lg"
      :title="title"
      :static="true"
      :lazy="true"
      hide-footer
    >
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <b-tabs content-class="mt-3">
          <b-tab title="Details" active>
            <template
              #title
              v-if="showTablesTab || showPartnersTab || showPagesTab"
            >
              Details&nbsp;<i
                v-if="detailsTabHasError"
                class="fas fa-exclamation-triangle"
              ></i>
            </template>

            <b-form-group label="Username" label-for="username-input">
              <b-form-input
                id="username-input"
                name="username-input"
                v-model="formValues.username"
                v-validate="{ required: true, min: 2, max: 25 }"
                :state="validateState('username-input')"
                aria-describedby="username-input-live-feedback"
                data-vv-as="Username"
                autocomplete="off"
              />
              <b-form-invalid-feedback id="name-input-live-feedback">{{
                veeErrors.first('username-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Email" label-for="email-input">
              <b-form-input
                id="email-input"
                name="email-input"
                v-model="formValues.email"
                v-validate="{ email: true }"
                :state="validateState('email-input')"
                aria-describedby="email-input-live-feedback"
                data-vv-as="Email"
                autocomplete="off"
              />
              <b-form-invalid-feedback id="name-input-live-feedback">{{
                veeErrors.first('email-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Password" label-for="password-input">
              <b-form-input
                id="password-input"
                name="password-input"
                v-model="formValues.password"
                v-validate="{
                  required: this.create,
                  min: 8,
                  regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])([a-zA-Z\d]|[^A-Za-z\d]){8,}$/
                }"
                :state="validateState('password-input')"
                aria-describedby="password-input-live-feedback"
                data-vv-as="Password"
                autocomplete="off"
              />
              <b-form-invalid-feedback id="name-input-live-feedback"
                >{{ veeErrors.first('password-input') }}. Password must contain
                a number, at least one special character, one capital and one
                lowercase letter.</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group label="Role" label-for="role-input">
              <b-form-select v-model="formValues.type" :options="typeOptions" />
            </b-form-group>

            <b-form-group>
              <b-form-checkbox
                v-model="formValues.loginConfirmation"
                :value="true"
                :unchecked-value="false"
                >&nbsp;Confirm login by e-mail</b-form-checkbox
              >
            </b-form-group>
          </b-tab>

          <b-tab title="Tables" v-if="showTablesTab">
            <b-form-group>
              <div class="mb-2">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="addAllTables"
                  :disabled="!this.$store.getters['tableGameTypeGroups/loaded']"
                >
                  <i class="fas fa-plus-circle"></i> Add All Tables
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="clearAllTables"
                  :disabled="
                    !formValues.tables || formValues.tables.length === 0
                  "
                >
                  <i class="fas fa-times-circle"></i> Clear All
                </b-button>
              </div>
              <treeselect
                v-model="formValues.tables"
                multiple
                value-consists-of="LEAF_PRIORITY"
                :options="filteredTableOptions"
                :disabled="!this.$store.getters['tableGameTypeGroups/loaded']"
                search-nested
              />
            </b-form-group>
          </b-tab>

          <b-tab title="Access" v-if="showPagesTab">
            <b-form-group>
              <div class="row">
                <div class="col">
                  <button
                    type="button"
                    v-on:click="toggleAllowedAccess"
                    class="btn"
                    :class="{
                      'btn-primary': this.formValues.allowedPages === null,
                      'btn-outline-primary':
                        this.formValues.allowedPages !== null
                    }"
                  >
                    {{
                      this.formValues.allowedPages === null
                        ? 'Edit per page access'
                        : 'Allow access to all pages'
                    }}
                  </button>
                </div>
              </div>

              <div
                class="table-wrapper mt-3"
                v-if="this.formValues.allowedPages !== null"
              >
                <table class="table b-table table-striped table-hover table-sm">
                  <tr v-for="item in menuItems" :key="item.path">
                    <td>
                      <i
                        :class="item.icon || 'nav-icon fas fa-tachometer-alt'"
                      ></i>
                      {{ item.name }}
                    </td>
                    <td style="width: 10px">
                      <i
                        v-if="hasAccess(item.path)"
                        class="fa fa-check text-success"
                      ></i>
                    </td>
                    <td style="width: 100px">
                      <button
                        type="button"
                        class="btn btn-xs"
                        :class="{
                          'btn-success': hasAccess(item.path),
                          'btn-outline-success': !hasAccess(item.path)
                        }"
                        v-on:click="toggleAccess(item.path)"
                      >
                        {{ !hasAccess(item.path) ? 'Enable' : 'Disable' }}
                      </button>
                    </td>
                  </tr>
                </table>
              </div>
            </b-form-group>
          </b-tab>

          <b-tab title="Partners" v-if="showPartnersTab">
            <b-form-group>
              <treeselect
                v-model="formValues.partners"
                multiple
                value-consists-of="LEAF_PRIORITY"
                :options="this.$store.getters['partnerGroups/list']"
                :disabled="!this.$store.getters['partnerGroups/loaded']"
                search-nested
              />
            </b-form-group>
          </b-tab>
        </b-tabs>
      </form>

      <b-button class="mt-2 float-right" variant="primary" @click="handleSubmit"
        >Save</b-button
      >
    </b-modal>
  </span>
</template>

<script>
import { getRouteIcon, ROUTE_GAME_TYPES } from '../../utils/routerIcons'
import Multiselect from 'vue-multiselect'
import Treeselect from '@riophae/vue-treeselect'
import Vue from 'vue'
import * as Roles from '../../definitions/Roles'
import { menuItems } from '../../modules/main/menu-sidebar/menu-items'

export default {
  data() {
    return {
      modalShow: false,
      formValues: this.getInitialValues(),
      menuItems: menuItems
    }
  },
  components: {
    Multiselect,
    Treeselect
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  methods: {
    addAllTables() {
      // Получаем все ID столов из древовидного списка
      this.formValues.tables = this.getAllTableIds()
    },
    clearAllTables() {
      this.formValues.tables = []
    },
    toggleAllowedAccess() {
      if (this.formValues.allowedPages === null) {
        this.formValues.allowedPages = []
      } else {
        this.formValues.allowedPages = null
      }
    },
    hasAccess(page) {
      if (this.formValues.allowedPages === null) {
        return true
      }
      return this.formValues.allowedPages.includes(page)
    },
    toggleAccess(page) {
      if (this.formValues.allowedPages === null) {
        this.formValues.allowedPages = []
      }

      let index = this.formValues.allowedPages.indexOf(page)
      if (index >= 0) {
        this.formValues.allowedPages.splice(index, 1)
      } else {
        this.formValues.allowedPages.push(page)
      }
    },
    getInitialValues() {
      return {
        id: null,
        username: null,
        email: null,
        password: null,
        partners: [],
        tables: [],
        type: null,
        loginConfirmation: false,
        allowedPages: null
      }
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref)
      }
      return null
    },
    setDefaults() {
      let initialValues = this.getInitialValues()

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field])
      }

      if (this.defaultValues) {
        for (let field in this.defaultValues) {
          Vue.set(this.formValues, field, this.defaultValues[field])
        }
      }
    },
    resetModal() {
      this.setDefaults()
    },
    hideModal() {
      this.$refs['modal-container'].hide()
    },
    toggle() {
      this.modalShow = !this.modalShow
    },
    handleSubmit() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return
        }

        this.$http({
          url: this.create
            ? '/api/v1/users'
            : '/api/v1/users/' + this.formValues.id,
          data: {
            ...this.formValues
          },
          method: this.create ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(() => {
            setTimeout(() => {
              this.$emit('finish')
            }, 500)

            this.hideModal()
            this.$toastr.i('Success')
          })
          .catch(err => {
            this.$toastr.e(JSON.stringify(err.response.data.message))

            throw err
          })
      })
    }
  },
  computed: {
    filteredTableOptions() {
      const currentUser = this.$store.getters.currentUser
      const tableGroups = this.$store.getters['tableGameTypeGroups/list'] || []

      // Если пользователь не загружен или это админ, показываем все столы
      if (!currentUser || currentUser.type === 1) {
        return tableGroups
      }

      // Если у пользователя есть ограничения по столам
      const userTables = currentUser.tables || []

      if (userTables.length === 0) {
        return []
      }

      // Фильтруем группы, оставляя только доступные столы
      return tableGroups
        .map(group => {
          const filteredChildren = (group.children || []).filter(table =>
            userTables.includes(table.id)
          )

          return {
            ...group,
            children: filteredChildren
          }
        })
        .filter(group => group.children.length > 0) // Удаляем пустые группы
    },
    typeOptions() {
      let typeOptions = [
        { value: Roles.ADMIN, text: Roles.humanRoles[Roles.ADMIN] },
        { value: Roles.USER, text: Roles.humanRoles[Roles.USER] }
      ]

      if (![Roles.ADMIN, Roles.USER].includes(this.formValues.type)) {
        typeOptions.push({
          value: this.formValues.type,
          text: Roles.humanRoles[this.formValues.type]
        })
      }

      return typeOptions
    },
    getAllTableIds() {
      return () => {
        const tableGroups = this.filteredTableOptions
        const allTableIds = []

        tableGroups.forEach(group => {
          if (group.children) {
            group.children.forEach(table => {
              allTableIds.push(table.id)
            })
          }
        })

        return allTableIds
      }
    },
    detailsTabHasError() {
      if (this.validateState('username-input') === false) {
        return true
      }

      if (this.validateState('email-input') === false) {
        return true
      }

      if (this.validateState('password-input') === false) {
        return true
      }

      return false
    },
    showTablesTab() {
      return (
        this.formValues.type &&
        [
          Roles.GAME_PROVIDER,
          Roles.GAME_PROVIDER_EXTENDED,
          Roles.USER
        ].includes(this.formValues.type)
      )
    },
    showPartnersTab() {
      return this.formValues.type && this.formValues.type === Roles.PARTNER
    },
    showPagesTab() {
      return this.formValues.type && this.formValues.type === Roles.USER
    },
    commonState() {
      return null
    },
    routeIcon: () => getRouteIcon(ROUTE_GAME_TYPES)
  },
  async beforeMount() {
    await Promise.all([
      this.$store.dispatch('loadCurrentUser'),
      this.$store.dispatch('tableGameTypeGroups/loadList'),
      this.$store.dispatch('partnerGroups/loadList')
    ])
  },
  mounted() {
    this.setDefaults()
  }
}
</script>

<style src="@riophae/vue-treeselect/dist/vue-treeselect.min.css"></style>
